import { useState, useEffect } from "react";
import { getPostUrl } from "../Utils/helpers";
import { getToken } from "../Config/common.config";

interface UseProtectedMediaReturn {
  objectUrl: string | null;
  loading: boolean;
  error: string | null;
}

const DIRECT_MEDIA_PREFIXES = [
  "/v1/uploads/",
  "/v1/media/profile-pictures/",
  "/v1/media/background-images/",
  "/v1/media/background-pictures/",
  "/v1/media/locked-preview/",
  "/v1/media/processed_images/",
  "/v1/media/processed_videos/",
  "/v1/media/others/",
  "processed_images/",
  "profile-pictures/",
  "background-images/",
  "processed_videos/",
  "others/",
];

const mediaDebug = (...args: unknown[]) => {
  if (typeof window !== "undefined" && window.localStorage.getItem("fanxoMediaDebug") === "1") {
    console.log("[fanxo-media]", ...args);
  }
};

export const isProtectedPostMediaUrl = (src: string) => {
  return src.includes("/v1/media/posts/") || src.startsWith("posts/");
};

const getDirectMediaUrl = (src: string) => {
  if (src.startsWith("blob:") || src.startsWith("data:")) {
    return src;
  }

  if (isProtectedPostMediaUrl(src)) {
    return null;
  }

  if (src.startsWith("http")) {
    return src;
  }

  if (DIRECT_MEDIA_PREFIXES.some((prefix) => src.startsWith(prefix))) {
    return getPostUrl(src);
  }

  return null;
};

const getProtectedPostMediaUrl = (src: string) => {
  if (!isProtectedPostMediaUrl(src)) {
    return null;
  }

  return src.startsWith("http") ? src : getPostUrl(src);
};

export const useProtectedMedia = (
  src: string | undefined
): UseProtectedMediaReturn => {
  const [objectUrl, setObjectUrl] = useState<string | null>(() => {
    if (!src) return null;
    return getDirectMediaUrl(src);
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let revokedUrl: string | null = null;
    const controller = new AbortController();

    if (!src) {
      setObjectUrl(null);
      setLoading(false);
      setError(null);
      return () => controller.abort();
    }

    const directMediaUrl = getDirectMediaUrl(src);
    if (directMediaUrl) {
      setObjectUrl(directMediaUrl);
      setLoading(false);
      setError(null);
      return () => controller.abort();
    }

    const protectedPostMediaUrl = getProtectedPostMediaUrl(src);
    if (!protectedPostMediaUrl) {
      setObjectUrl(null);
      setLoading(false);
      setError("Unsupported media source");
      return () => controller.abort();
    }

    const token = getToken();
    if (!token) {
      setObjectUrl(null);
      setLoading(false);
      setError("Authentication required");
      return () => controller.abort();
    }

    mediaDebug("blob fetch start", protectedPostMediaUrl);
    setLoading(true);
    setError(null);

    fetch(protectedPostMediaUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Media request failed (${response.status})`);
        }
        return response.blob();
      })
      .then((blob) => {
        if (controller.signal.aborted) return;
        mediaDebug("blob fetch complete and size", { src: protectedPostMediaUrl, size: blob.size });
        revokedUrl = URL.createObjectURL(blob);
        mediaDebug("objectURL created", { src: protectedPostMediaUrl });
        setObjectUrl(revokedUrl);
        setLoading(false);
      })
      .catch((fetchError) => {
        if (controller.signal.aborted) return;
        setObjectUrl(null);
        setLoading(false);
        setError(fetchError instanceof Error ? fetchError.message : "Failed to load media");
      });

    return () => {
      controller.abort();
      if (revokedUrl) {
        URL.revokeObjectURL(revokedUrl);
      }
    };
  }, [src]);

  return { objectUrl, loading, error };
};

